export interface NavSection<T> {
  id: string;
  labelKey: string;
  items: T[];
}

interface GroupDef {
  id: string;
  labelKey: string;
}

const SIDEBAR_GROUPS: GroupDef[] = [
  { id: 'overview', labelKey: 'sidebar.groups.overview' },
  { id: 'operations', labelKey: 'sidebar.groups.operations' },
  { id: 'followUp', labelKey: 'sidebar.groups.followUp' },
  { id: 'system', labelKey: 'sidebar.groups.system' },
  { id: 'other', labelKey: 'sidebar.groups.other' },
];

const SIDEBAR_SYSTEM_GROUP: Record<string, string> = {
  dashboard: 'overview',
  realtime: 'overview',
  deque: 'operations',
  fleet: 'operations',
  workorder: 'operations',
  booking: 'operations',
  workshop: 'operations',
  keymanagement: 'operations',
  'temperature-monitoring': 'operations',
  homecare: 'operations',
  reports: 'followUp',
  analysis: 'followUp',
  crm: 'followUp',
  administration: 'system',
  sysadmin: 'system',
  mobilapp: 'system',
};

const GROUP_LABEL: Record<string, string> = {
  daily: 'systemPage.groups.daily',
  planning: 'systemPage.groups.planning',
  resources: 'systemPage.groups.resources',
  history: 'systemPage.groups.history',
  settings: 'systemPage.groups.settings',
  vehicles: 'systemPage.groups.vehicles',
  devices: 'systemPage.groups.devices',
  economy: 'systemPage.groups.economy',
  compliance: 'systemPage.groups.compliance',
  driving: 'systemPage.groups.driving',
  environment: 'systemPage.groups.environment',
  fleet: 'systemPage.groups.fleet',
  analysis: 'systemPage.groups.analysis',
  customers: 'systemPage.groups.customers',
  catalog: 'systemPage.groups.catalog',
  people: 'systemPage.groups.people',
  data: 'systemPage.groups.data',
  monitor: 'systemPage.groups.monitor',
  overview: 'systemPage.groups.overview',
  map: 'systemPage.groups.map',
  events: 'systemPage.groups.events',
  functions: 'systemPage.groups.functions',
  other: 'systemPage.groups.other',
};

function defs(ids: string[]): GroupDef[] {
  return [
    ...ids.map((id) => ({ id, labelKey: GROUP_LABEL[id] })),
    { id: 'other', labelKey: GROUP_LABEL.other },
  ];
}

const MODULE_GROUP_ORDER: Record<string, GroupDef[]> = {
  booking: defs(['daily', 'planning', 'resources', 'history', 'settings']),
  deque: defs(['daily', 'vehicles', 'devices', 'economy', 'compliance', 'analysis']),
  fleet: defs(['daily', 'vehicles', 'devices', 'economy', 'compliance', 'analysis']),
  reports: defs(['driving', 'environment', 'fleet', 'analysis']),
  analysis: defs(['overview', 'map', 'events']),
  crm: defs(['customers', 'economy', 'catalog']),
  administration: defs(['people', 'data', 'settings']),
  sysadmin: defs(['monitor', 'data', 'settings']),
  workshop: defs(['daily', 'economy']),
  keymanagement: defs(['daily', 'history', 'settings']),
  workorder: defs(['daily', 'planning']),
  'temperature-monitoring': defs(['daily', 'settings']),
  homecare: defs(['daily']),
  dashboard: defs(['overview']),
  mobilapp: defs(['daily']),
};

const BOOKING_GROUPS: Record<string, string> = {
  calendar: 'daily',
  'quick-booking': 'daily',
  'bookings-search': 'daily',
  requests: 'daily',
  collision_bookings: 'daily',
  'collision-bookings': 'daily',
  'pool-watch': 'daily',
  'booking-optimization': 'planning',
  resource_manager: 'planning',
  'travel-planner': 'planning',
  vehicle_replacement: 'planning',
  'vehicle-replacement': 'planning',
  'vehicle-pools': 'resources',
  'booking-resources': 'resources',
  'pickup-locations': 'resources',
  'rental-management': 'resources',
  suppliers: 'resources',
  'booking-basis': 'history',
  'own-car-trip': 'history',
  'own-car-trips': 'history',
  'old-booking': 'history',
  'booking-stats': 'history',
  'booking-settings': 'settings',
  destinations: 'settings',
  'cost-centers': 'settings',
};

const DEQUE_GROUPS: Record<string, string> = {
  fleet: 'daily',
  'fleet-overview': 'daily',
  drivers: 'daily',
  organization: 'daily',
  assignments: 'daily',
  'map-services': 'daily',
  'live-tracking': 'daily',
  'track-playback': 'daily',
  'personal-trackers': 'daily',
  'vehicle-log': 'daily',
  'vehicle-categories': 'vehicles',
  'equipment-tags': 'vehicles',
  'usage-statuses': 'vehicles',
  'alternative-fuels': 'vehicles',
  'replacement-chain': 'vehicles',
  'vehicle-procurement': 'vehicles',
  charging: 'vehicles',
  'service-overview': 'vehicles',
  devices: 'devices',
  geofences: 'devices',
  'geofence-events': 'devices',
  'unplug-events': 'devices',
  'fuel-management': 'economy',
  'fuel-cards': 'economy',
  'vehicle-economy': 'economy',
  'nordea-leasing': 'economy',
  'protector-insurance': 'economy',
  'cost-invoices': 'economy',
  'license-check': 'compliance',
  tidbok: 'compliance',
  heatmap: 'analysis',
  'heatmap-import': 'analysis',
  'crash-detection': 'analysis',
  'ai-analysis': 'analysis',
};

const MODULE_GROUP_BY_NAME: Record<string, Record<string, string>> = {
  booking: BOOKING_GROUPS,
  deque: DEQUE_GROUPS,
  fleet: DEQUE_GROUPS,
  reports: {
    trips: 'driving',
    'driving-log': 'driving',
    'work-trips': 'driving',
    odometer: 'driving',
    activity: 'driving',
    'stop-time': 'driving',
    eco: 'environment',
    'eco-driving': 'environment',
    'energy-consumption': 'environment',
    co2: 'environment',
    fuel: 'environment',
    'fuel-analysis': 'environment',
    underutilized: 'fleet',
    'vehicle-wear': 'fleet',
    'vehicle-kpi': 'fleet',
    rental: 'fleet',
    'rental-report': 'fleet',
    'fleet-tco': 'fleet',
    executive: 'fleet',
    'speed-compliance': 'fleet',
    'service-costs': 'fleet',
    'geofence-zones': 'analysis',
    'route-analysis': 'analysis',
    dtc: 'analysis',
    'dtc-report': 'analysis',
    'tire-analysis': 'analysis',
    'report-generator': 'analysis',
    schedules: 'analysis',
    statistics: 'analysis',
  },
  analysis: {
    statistics: 'overview',
    trends: 'overview',
    benchmarks: 'overview',
    heatmap: 'map',
    'heatmap-import': 'map',
    'route-analysis': 'map',
    'crash-detection': 'events',
    'ai-analysis': 'events',
    'jam-detection': 'events',
  },
  crm: {
    customers: 'customers',
    projects: 'customers',
    pipeline: 'customers',
    procurements: 'customers',
    economy: 'economy',
    invoices: 'economy',
    pricelist: 'economy',
    'module-catalog': 'catalog',
  },
  administration: {
    users: 'people',
    roles: 'people',
    organizations: 'people',
    support: 'people',
    'support-chat': 'people',
    imports: 'data',
    translations: 'data',
    packages: 'data',
    devices: 'data',
    'device-management': 'data',
    'sim-cards': 'data',
    geofences: 'data',
    'geofence-events': 'data',
    'carinfo-org-sync': 'data',
    'vehicle-carinfo-sync': 'data',
    'eye-inventory': 'data',
    'fota-migration': 'data',
    'cost-centers': 'settings',
    'theme-settings': 'settings',
    'guide-management': 'settings',
    'carinfo-filter': 'settings',
    'service-workshops': 'settings',
    'econ-kodverk': 'settings',
    moten: 'settings',
    'flinkey-boxes': 'settings',
    'damage-reports': 'settings',
    'trip-analysis': 'settings',
    'device-baseline': 'settings',
    'obd-vehicles': 'settings',
    'obd-knowledge': 'settings',
    'dev-projects': 'settings',
    'jam-detection': 'settings',
  },
  sysadmin: {
    overview: 'monitor',
    'system-monitor': 'monitor',
    'api-metrics': 'monitor',
    'slow-queries': 'monitor',
    logs: 'monitor',
    'error-log': 'monitor',
    'audit-log': 'monitor',
    'general-audit-log': 'monitor',
    'ai-log': 'monitor',
    'sso-log': 'monitor',
    'data-migration': 'data',
    'data-replay': 'data',
    replay: 'data',
    'trip-validation': 'data',
    'gps-data-lag': 'data',
    'import-sources': 'data',
    'device-commands': 'data',
    'device-queues': 'data',
    'eco-settings': 'settings',
  },
  workshop: {
    service: 'daily',
    repairs: 'daily',
    inspections: 'daily',
    'service-overview': 'daily',
    'service-costs': 'economy',
  },
  keymanagement: {
    'key-cabinet': 'daily',
    'customer-keys': 'daily',
    'key-management': 'daily',
    'flinkey-boxes': 'daily',
    overview: 'daily',
    'key-log': 'history',
    'cabinet-sync': 'settings',
  },
  workorder: {
    craftsman: 'daily',
    delivery: 'daily',
    assignments: 'daily',
    overview: 'daily',
    'service-agreements': 'planning',
  },
  'temperature-monitoring': {
    overview: 'daily',
    sensors: 'daily',
    reports: 'daily',
    config: 'settings',
    recipients: 'settings',
    schedules: 'settings',
  },
  homecare: {
    'homecare-planning': 'daily',
    'homecare-clients': 'daily',
    'homecare-staff': 'daily',
  },
  mobilapp: {
    flinky: 'daily',
    dqbooking: 'daily',
    arbetsorder: 'daily',
    flinkyboxes: 'daily',
    'delad-bil': 'daily',
    'driving-log-mobile': 'daily',
    'damage-report': 'daily',
    'find-vehicle-mobile': 'daily',
    'mobile-access': 'daily',
    'support-chat': 'daily',
  },
};

export function compareLabels(a: string, b: string, locale: string): number {
  return a.localeCompare(b, locale || 'sv', { sensitivity: 'base', numeric: true });
}

/** Översikt har fast ordning: Dashboard, sedan Realtidskarta. */
const SIDEBAR_OVERVIEW_ORDER = ['dashboard', 'realtime'];

export function sidebarGroupId(systemName: string): string {
  return SIDEBAR_SYSTEM_GROUP[systemName] ?? 'other';
}

function sortSidebarItems<T extends { name: string }>(
  groupId: string,
  items: T[],
  labelOf: (item: T) => string,
  locale: string
): T[] {
  if (groupId !== 'overview') {
    return [...items].sort((a, b) => compareLabels(labelOf(a), labelOf(b), locale));
  }
  const rank = new Map(SIDEBAR_OVERVIEW_ORDER.map((name, index) => [name, index]));
  return [...items].sort((a, b) => {
    const aRank = rank.get(a.name) ?? SIDEBAR_OVERVIEW_ORDER.length;
    const bRank = rank.get(b.name) ?? SIDEBAR_OVERVIEW_ORDER.length;
    if (aRank !== bRank) return aRank - bRank;
    return compareLabels(labelOf(a), labelOf(b), locale);
  });
}

export function moduleGroupId(systemName: string, moduleName: string): string {
  if (systemName === 'dashboard' && moduleName.startsWith('widget-')) return 'overview';
  const systemMap = MODULE_GROUP_BY_NAME[systemName];
  if (!systemMap) return 'functions';
  return systemMap[moduleName] ?? 'other';
}

function groupItems<T>(
  items: T[],
  groupIdOf: (item: T) => string,
  order: GroupDef[],
  labelOf: (item: T) => string,
  locale: string
): NavSection<T>[] {
  const buckets = new Map<string, T[]>();
  for (const item of items) {
    const id = groupIdOf(item);
    const list = buckets.get(id) ?? [];
    list.push(item);
    buckets.set(id, list);
  }

  const known = new Set(order.map((group) => group.id));
  const sections: NavSection<T>[] = [];
  const push = (id: string, labelKey: string, list: T[]) => {
    sections.push({
      id,
      labelKey,
      items: [...list].sort((a, b) => compareLabels(labelOf(a), labelOf(b), locale)),
    });
  };

  for (const group of order) {
    const list = buckets.get(group.id);
    if (list?.length) push(group.id, group.labelKey, list);
  }
  for (const [id, list] of buckets) {
    if (known.has(id) || list.length === 0) continue;
    push(id, GROUP_LABEL[id] ?? GROUP_LABEL.other, list);
  }
  return sections;
}

export function groupSidebarSystems<T extends { name: string }>(
  systems: T[],
  labelOf: (system: T) => string,
  locale: string
): NavSection<T>[] {
  return groupItems(systems, (system) => sidebarGroupId(system.name), SIDEBAR_GROUPS, labelOf, locale).map(
    (section) => ({
      ...section,
      items: sortSidebarItems(section.id, section.items, labelOf, locale),
    })
  );
}

export function groupModules<T extends { name: string }>(
  systemName: string,
  modules: T[],
  labelOf: (module: T) => string,
  locale: string
): NavSection<T>[] {
  const order = MODULE_GROUP_ORDER[systemName] ?? [{ id: 'functions', labelKey: GROUP_LABEL.functions }];
  return groupItems(modules, (module) => moduleGroupId(systemName, module.name), order, labelOf, locale);
}

export function moduleCountLabel(translate: (key: string) => unknown, count: number): string {
  const key = count === 1 ? 'systemPage.functionCountOne' : 'systemPage.functionCount';
  const raw = translate(key);
  const fallback = count === 1 ? '{count} funktion' : '{count} funktioner';
  const template = typeof raw === 'string' && raw !== key ? raw : fallback;
  return template.replace('{count}', String(count));
}
